/**
 * Class representing a Pixabay image item.
 * Wraps the raw API object and provides helpful utilities.
 */
export class PixabayImage {
    /**
     * Create a Pixabay image wrapper.
     * @param {PixabayRawImage} raw - Raw Pixabay image object.
     */
    constructor(raw= {}) {
        /** @type {PixabayRawImage} */
        this.raw = raw;

        // Basic properties
        this.id = raw.id;
        this.type = raw.type || "photo";
        this.tags = this.parseTags(raw.tags);

        // URLs
        this.previewURL = raw.previewUrl;
        this.webformatURL = raw.webformatUrl;
        this.largeImageURL = raw.largeImageUrl;

        // Dimensions
        this.previewWidth = raw.previewWidth;
        this.previewHeight = raw.previewHeight;
        this.webformatWidth = raw.webformatWidth;
        this.webformatHeight = raw.webformatHeight;
        this.imageWidth = raw.imageWidth;
        this.imageHeight = raw.imageHeight;

        // Metadata
        this.pageURL = raw.pageUrl;
        this.user = raw.user;
        this.userURL = raw.userUrl;
        this.userImageURL = raw.userImageUrl;
        this.likes = raw.likes || 0;
        this.views = raw.views || 0;
        this.downloads = raw.downloads || 0;
        this.size = raw.imageSize || 0;

        // AI / rating info
        this.isAiGenerated = raw.isAiGenerated || false;
        this.isGRated = raw.isGRated || false;
        this.isLowQuality = raw.isLowQuality || false;
    }

    /**
     * Parse the comma-separated tag string into an array.
     * @param {string} rawTags
     * @returns {string[]}
     */
    parseTags(rawTags) {
        if (!rawTags) return [];
        return rawTags
            .split(",")
            .map(t => t.trim())
            .filter(Boolean);
    }

    /**
     * Returns the best available image URL depending on desired quality.
     * @param {"preview"|"webformat"|"large"} size
     * @returns {string|null}
     */
    getUrl(size = "webformat") {
        switch (size) {
            case "preview":
                return this.previewURL || this.webformatURL || this.largeImageURL || null;
            case "large":
                return this.largeImageURL || this.webformatURL || this.previewURL || null;
            default:
                return this.webformatURL || this.previewURL || this.largeImageURL || null;
        }
    }

    /**
     * Returns a square thumbnail URL for list views.
     * @returns {string|null}
     */
    get thumbnail() {
        return this.previewURL || this.webformatURL || null;
    }

    /**
     * Returns true if image is portrait orientation.
     * @returns {boolean}
     */
    get isPortrait() {
        return this.imageHeight > this.imageWidth;
    }

    /**
     * Returns true if image is landscape orientation.
     * @returns {boolean}
     */
    get isLandscape() {
        return this.imageWidth >= this.imageHeight;
    }

    /**
     * Get aspect ratio (width / height).
     * @returns {number}
     */
    get aspectRatio() {
        if (!this.imageWidth || !this.imageHeight) return 1;
        return this.imageWidth / this.imageHeight;
    }

    /**
     * Basic author information.
     * @returns {{ name: string, url: string, avatar: string }}
     */
    get author() {
        return {
            name: this.user || "",
            url: this.userURL || "",
            avatar: this.userImageURL || ""
        };
    }

    /**
     * Convert the object to a minimal clean export shape.
     * Useful when saving to your own DB or internal system.
     * @returns {Object}
     */
    toObject() {
        return {
            id: this.id,
            url: this.pageURL,
            preview: this.previewURL,
            webformat: this.webformatURL,
            large: this.largeImageURL,
            width: this.imageWidth,
            height: this.imageHeight,
            tags: this.tags,
            author: this.author,
            likes: this.likes,
            views: this.views,
            downloads: this.downloads,
            isAiGenerated: this.isAiGenerated
        };
    }
}
